<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';

const props = defineProps<{ storeId: string }>();

const { t } = useI18n({ useScope: 'global' });
const qc = useQueryClient();

type ContactLanguage = 'fr' | 'en' | 'nl';
type ContactPosition =
  | 'ceo'
  | 'cfo'
  | 'cto'
  | 'manager'
  | 'accountant'
  | 'legal_representative'
  | 'technical_contact'
  | 'billing_contact'
  | 'support_contact';

interface StoreContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  position: ContactPosition | null;
  languages: ContactLanguage[];
  isPrimary: boolean;
}

const POSITIONS: ContactPosition[] = [
  'ceo',
  'cfo',
  'cto',
  'manager',
  'accountant',
  'legal_representative',
  'technical_contact',
  'billing_contact',
  'support_contact',
];
const LANGUAGES: ContactLanguage[] = ['fr', 'en', 'nl'];

function positionLabel(p: ContactPosition | null) {
  return p ? t(`contact.positionOptions.${p}`) : '—';
}

const { data: contacts, isPending: loading } = useQuery({
  queryKey: computed(() => ['store-contacts', props.storeId]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `restaurants/${props.storeId}/contacts/search`,
    );
    const d = r.data as any;
    return (d?.data ?? d ?? []) as StoreContact[];
  },
  enabled: computed(() => !!props.storeId),
});

/* ── Formulaire ajout/édition ────────────────────────────────────────────── */
type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: ContactPosition | '';
  languages: ContactLanguage[];
  isPrimary: boolean;
};

function emptyForm(): FormState {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    languages: [],
    isPrimary: false,
  };
}

const showForm = ref(false);
const editingId = ref<string | null>(null);
const form = reactive<FormState>(emptyForm());
const attemptedSave = ref(false);
const saving = ref(false);
const formError = ref<string | null>(null);

const showFirstNameError = computed(
  () => attemptedSave.value && !form.firstName.trim(),
);
const showLastNameError = computed(
  () => attemptedSave.value && !form.lastName.trim(),
);
const showEmailError = computed(
  () => attemptedSave.value && !form.email.trim(),
);

function openNewForm() {
  editingId.value = null;
  Object.assign(form, emptyForm());
  attemptedSave.value = false;
  formError.value = null;
  showForm.value = true;
}

function openEditForm(c: StoreContact) {
  editingId.value = c.id;
  form.firstName = c.firstName;
  form.lastName = c.lastName;
  form.email = c.email;
  form.phone = c.phone ?? '';
  form.position = c.position ?? '';
  form.languages = [...(c.languages ?? [])];
  form.isPrimary = c.isPrimary;
  attemptedSave.value = false;
  formError.value = null;
  showForm.value = true;
}

function closeForm() {
  showForm.value = false;
  editingId.value = null;
}

function toggleLanguage(l: ContactLanguage) {
  const idx = form.languages.indexOf(l);
  if (idx >= 0) form.languages.splice(idx, 1);
  else form.languages.push(l);
}

async function saveContact() {
  attemptedSave.value = true;
  if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
    return;
  }
  saving.value = true;
  formError.value = null;
  try {
    const payload: Record<string, unknown> = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      isPrimary: form.isPrimary,
      languages: form.languages,
    };
    if (form.phone.trim()) payload.phone = form.phone.trim();
    if (form.position) payload.position = form.position;

    if (editingId.value) {
      await axiosInstance.put(
        `restaurants/${props.storeId}/contacts/update/${editingId.value}`,
        payload,
      );
    } else {
      await axiosInstance.post(
        `restaurants/${props.storeId}/contacts/add`,
        payload,
      );
    }
    qc.invalidateQueries({ queryKey: ['store-contacts', props.storeId] });
    closeForm();
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    formError.value = Array.isArray(raw)
      ? raw[0]
      : ((typeof raw === 'string' ? raw : null) ??
        err?.message ??
        t('contact.saveError'));
  } finally {
    saving.value = false;
  }
}

/* ── Suppression ──────────────────────────────────────────────────────────── */
const confirmDeleteContact = ref<StoreContact | null>(null);
const deleting = ref(false);

async function doDeleteContact() {
  if (!confirmDeleteContact.value || deleting.value) return;
  deleting.value = true;
  try {
    await axiosInstance.delete(
      `restaurants/${props.storeId}/contacts/delete/${confirmDeleteContact.value.id}`,
    );
    qc.invalidateQueries({ queryKey: ['store-contacts', props.storeId] });
    confirmDeleteContact.value = null;
  } catch {
    /* laisse la modale ouverte, l'utilisateur peut réessayer */
  } finally {
    deleting.value = false;
  }
}

watch(
  () => props.storeId,
  () => {
    closeForm();
    confirmDeleteContact.value = null;
  },
);
</script>

<template>
  <div class="cct">
    <div class="cct__head">
      <div>
        <h2 class="cct__title">{{ t('contact.title') }}</h2>
        <p class="cct__sub">{{ t('contact.subtitle') }}</p>
      </div>
      <button v-if="!showForm" class="cct__add-btn" @click="openNewForm">
        <i class="pi pi-plus" /> {{ t('contact.newContact') }}
      </button>
    </div>

    <!-- Formulaire ajout / édition -->
    <div v-if="showForm" class="cct__form-card">
      <p class="cct__form-title">
        {{ editingId ? t('contact.editContact') : t('contact.newContact') }}
      </p>

      <div class="cct__field-row">
        <div class="cct__field">
          <label class="cct__label"
            >{{ t('contact.firstName') }} <span class="cct__req">*</span></label
          >
          <input
            v-model="form.firstName"
            type="text"
            class="cct__input"
            :class="{ 'cct__input--error': showFirstNameError }"
          />
          <p v-if="showFirstNameError" class="cct__field-error">
            {{ t('product.requiredField') }}
          </p>
        </div>
        <div class="cct__field">
          <label class="cct__label"
            >{{ t('contact.lastName') }} <span class="cct__req">*</span></label
          >
          <input
            v-model="form.lastName"
            type="text"
            class="cct__input"
            :class="{ 'cct__input--error': showLastNameError }"
          />
          <p v-if="showLastNameError" class="cct__field-error">
            {{ t('product.requiredField') }}
          </p>
        </div>
      </div>

      <div class="cct__field-row">
        <div class="cct__field">
          <label class="cct__label"
            >{{ t('contact.email') }} <span class="cct__req">*</span></label
          >
          <input
            v-model="form.email"
            type="email"
            class="cct__input"
            :class="{ 'cct__input--error': showEmailError }"
          />
          <p v-if="showEmailError" class="cct__field-error">
            {{ t('product.requiredField') }}
          </p>
        </div>
        <div class="cct__field">
          <label class="cct__label">{{ t('contact.phone') }}</label>
          <input
            v-model="form.phone"
            type="text"
            class="cct__input"
            placeholder="+32..."
          />
        </div>
      </div>

      <div class="cct__field-row">
        <div class="cct__field">
          <label class="cct__label">{{ t('contact.position') }}</label>
          <div class="cct__select-wrap">
            <select v-model="form.position" class="cct__select">
              <option value="">—</option>
              <option v-for="p in POSITIONS" :key="p" :value="p">
                {{ positionLabel(p) }}
              </option>
            </select>
            <i class="pi pi-chevron-down cct__select-icon" />
          </div>
        </div>
        <div class="cct__field">
          <label class="cct__label">{{ t('contact.isPrimary') }}</label>
          <label class="cct__toggle">
            <input
              type="checkbox"
              v-model="form.isPrimary"
              class="cct__toggle-input"
            />
            <span class="cct__toggle-track"
              ><span class="cct__toggle-thumb"
            /></span>
          </label>
        </div>
      </div>

      <div class="cct__field">
        <label class="cct__label">{{ t('contact.languages') }}</label>
        <div class="cct__lang-chips">
          <button
            v-for="l in LANGUAGES"
            :key="l"
            type="button"
            class="cct__lang-chip"
            :class="{ 'cct__lang-chip--active': form.languages.includes(l) }"
            @click="toggleLanguage(l)"
          >
            {{ l.toUpperCase() }}
          </button>
        </div>
      </div>

      <div v-if="formError" class="cct__error-banner">
        <i class="pi pi-exclamation-circle" /> {{ formError }}
      </div>

      <div class="cct__form-actions">
        <button
          class="cct__btn cct__btn--ghost"
          :disabled="saving"
          @click="closeForm"
        >
          <i class="pi pi-times" /> {{ t('product.cancel') }}
        </button>
        <button
          class="cct__btn cct__btn--primary"
          :disabled="saving"
          @click="saveContact"
        >
          <span v-if="saving" class="cct__spinner" />
          <i v-else class="pi pi-check" />
          {{
            saving
              ? t('product.saving')
              : editingId
                ? t('product.edit')
                : t('product.create')
          }}
        </button>
      </div>
    </div>

    <!-- Liste des contacts -->
    <div v-if="loading" class="cct__loading">
      <span class="cct__spinner" /> {{ t('product.loading') }}
    </div>
    <div v-else-if="!contacts?.length && !showForm" class="cct__empty">
      <i class="pi pi-address-book" />
      <p>{{ t('contact.noContact') }}</p>
    </div>
    <div v-else class="cct__list">
      <div v-for="c in contacts" :key="c.id" class="cct__row">
        <div class="cct__row-avatar">
          {{ (c.firstName.charAt(0) + c.lastName.charAt(0)).toUpperCase() }}
        </div>
        <div class="cct__row-info">
          <div class="cct__row-name-line">
            <span class="cct__row-name"
              >{{ c.firstName }} {{ c.lastName }}</span
            >
            <span v-if="c.isPrimary" class="cct__chip cct__chip--primary">{{
              t('contact.primaryBadge')
            }}</span>
            <span v-if="c.position" class="cct__chip">{{
              positionLabel(c.position)
            }}</span>
          </div>
          <div class="cct__row-meta">
            <span class="cct__row-meta-item"
              ><i class="pi pi-envelope" /> {{ c.email }}</span
            >
            <span v-if="c.phone" class="cct__row-meta-item"
              ><i class="pi pi-phone" /> {{ c.phone }}</span
            >
            <span v-if="c.languages?.length" class="cct__row-meta-item"
              ><i class="pi pi-globe" />
              {{ c.languages.map((l) => l.toUpperCase()).join(', ') }}</span
            >
          </div>
        </div>
        <div class="cct__row-actions">
          <button class="cct__icon-btn" @click="openEditForm(c)">
            <i class="pi pi-pencil" />
          </button>
          <button
            class="cct__icon-btn cct__icon-btn--danger"
            @click="confirmDeleteContact = c"
          >
            <i class="pi pi-trash" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modale suppression -->
    <Teleport to="body">
      <div
        v-if="confirmDeleteContact"
        class="cct__modal-backdrop"
        @click.self="confirmDeleteContact = null"
      >
        <div class="cct__modal">
          <div class="cct__modal-ico"><i class="pi pi-trash" /></div>
          <h2 class="cct__modal-title">
            {{ t('contact.deleteConfirmTitle') }}
          </h2>
          <p class="cct__modal-text">{{ t('contact.deleteConfirmBody') }}</p>
          <div class="cct__modal-actions">
            <button
              class="cct__btn cct__btn--ghost"
              :disabled="deleting"
              @click="confirmDeleteContact = null"
            >
              {{ t('product.cancel') }}
            </button>
            <button
              class="cct__btn cct__btn--danger"
              :disabled="deleting"
              @click="doDeleteContact"
            >
              <span v-if="deleting" class="cct__spinner" />
              <i v-else class="pi pi-trash" />
              {{ deleting ? t('product.deleting') : t('product.delete') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.cct {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cct__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.cct__title {
  margin: 0 0 0.25rem;
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.cct__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}
.cct__add-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 1.125rem;
  border-radius: 0.75rem;
  border: none;
  background: #0b1320;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s;
}
.cct__add-btn:hover {
  opacity: 0.88;
}
[data-theme='dark'] .cct__add-btn {
  background: #fff;
  color: #0b1320;
}

/* Form */
.cct__form-card {
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  border-radius: 1rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.cct__form-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.cct__field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.875rem;
}
@media (max-width: 640px) {
  .cct__field-row {
    grid-template-columns: 1fr;
  }
}
.cct__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cct__label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--nxt-on-surface-variant);
}
.cct__req {
  color: var(--nxt-error, #ba1a1a);
}
.cct__input {
  height: 2.625rem;
  border-radius: 0.625rem;
  border: 1.5px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: var(--nxt-surface-low, #f8f9fa);
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  padding: 0 0.75rem;
  outline: none;
  transition: border-color 0.15s;
}
.cct__input:focus {
  border-color: #0b1320;
}
[data-theme='dark'] .cct__input:focus {
  border-color: #fff;
}
.cct__input--error {
  border-color: var(--nxt-error, #ba1a1a);
}
.cct__field-error {
  margin: 0;
  font-size: 0.75rem;
  color: var(--nxt-error, #ba1a1a);
  font-weight: 600;
}
.cct__select-wrap {
  position: relative;
}
.cct__select {
  width: 100%;
  height: 2.625rem;
  padding: 0 2rem 0 0.75rem;
  border-radius: 0.625rem;
  border: 1.5px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: var(--nxt-surface-low, #f8f9fa);
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  outline: none;
  appearance: none;
  cursor: pointer;
}
.cct__select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.7rem;
  color: var(--nxt-on-surface-variant);
  pointer-events: none;
}
.cct__toggle {
  display: inline-block;
  cursor: pointer;
}
.cct__toggle-input {
  display: none;
}
.cct__toggle-track {
  display: inline-flex;
  width: 2.75rem;
  height: 1.625rem;
  border-radius: 9999px;
  background: var(--nxt-surface-highest, #e1e3e4);
  position: relative;
  transition: background 0.25s;
}
.cct__toggle-input:checked + .cct__toggle-track {
  background: #0b1320;
}
[data-theme='dark'] .cct__toggle-input:checked + .cct__toggle-track {
  background: #fff;
}
.cct__toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: transform 0.25s;
}
[data-theme='dark'] .cct__toggle-thumb {
  background: #0b1320;
}
.cct__toggle-input:checked + .cct__toggle-track .cct__toggle-thumb {
  transform: translateX(1.125rem);
}
.cct__lang-chips {
  display: flex;
  gap: 0.5rem;
}
.cct__lang-chip {
  height: 2.25rem;
  padding: 0 0.875rem;
  border-radius: 0.5rem;
  border: 1.5px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: transparent;
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
}
.cct__lang-chip--active {
  background: #0b1320;
  border-color: #0b1320;
  color: #fff;
}
[data-theme='dark'] .cct__lang-chip--active {
  background: #fff;
  border-color: #fff;
  color: #0b1320;
}
.cct__error-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: rgba(186, 26, 26, 0.06);
  border: 1px solid rgba(186, 26, 26, 0.15);
  color: var(--nxt-error, #ba1a1a);
  font-size: 0.8125rem;
  font-weight: 600;
}
.cct__form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
}
.cct__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 2.5rem;
  padding: 0 1.125rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
}
.cct__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cct__btn--primary {
  background: #0b1320;
  color: #fff;
}
[data-theme='dark'] .cct__btn--primary {
  background: #fff;
  color: #0b1320;
}
.cct__btn--ghost {
  background: transparent;
  border: 1.5px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  color: var(--nxt-on-surface);
}
.cct__btn--ghost:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.04);
}
[data-theme='dark'] .cct__btn--ghost:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
}
.cct__btn--danger {
  background: #dc2626;
  color: #fff;
}
.cct__btn--danger:hover:not(:disabled) {
  opacity: 0.9;
}

/* Liste */
.cct__loading,
.cct__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
  padding: 2.5rem 1rem;
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
}
.cct__empty .pi {
  font-size: 1.75rem;
  opacity: 0.4;
}
.cct__empty p {
  margin: 0;
}
.cct__list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.cct__row {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  border-radius: 0.875rem;
}
.cct__row-avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--nxt-surface-high, #e7e8e9);
  color: var(--nxt-on-surface-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 800;
}
.cct__row-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.cct__row-name-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.cct__row-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cct__chip {
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  background: var(--nxt-surface-high, #e7e8e9);
  color: var(--nxt-on-surface-variant);
}
.cct__chip--primary {
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
}
.cct__row-meta {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex-wrap: wrap;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}
.cct__row-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}
.cct__row-actions {
  display: flex;
  gap: 0.375rem;
  flex-shrink: 0;
}
.cct__icon-btn {
  width: 2.125rem;
  height: 2.125rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: transparent;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.cct__icon-btn:hover {
  background: rgba(0, 0, 0, 0.04);
}
.cct__icon-btn--danger:hover {
  border-color: #dc2626;
  color: #dc2626;
  background: rgba(220, 38, 38, 0.06);
}

.cct__spinner {
  width: 0.875rem;
  height: 0.875rem;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  animation: cct-spin 0.6s linear infinite;
}
.cct__btn--ghost .cct__spinner,
.cct__loading .cct__spinner {
  border-color: rgba(0, 0, 0, 0.15);
  border-top-color: var(--nxt-on-surface-variant, #666);
}
@keyframes cct-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Modale suppression */
.cct__modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
}
.cct__modal {
  background: var(--nxt-card-bg, #fff);
  border-radius: 1rem;
  padding: 1.75rem;
  max-width: 24rem;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}
[data-theme='dark'] .cct__modal {
  background: #1e2025;
}
.cct__modal-ico {
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}
.cct__modal-title {
  margin: 0 0 0.5rem;
  font-size: 1.0625rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.cct__modal-text {
  margin: 0 0 1.25rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant, #666);
  line-height: 1.5;
}
.cct__modal-actions {
  display: flex;
  gap: 0.625rem;
  justify-content: center;
}

[data-theme='dark'] .cct__form-card,
[data-theme='dark'] .cct__row {
  background: #1e2025;
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .cct__input,
[data-theme='dark'] .cct__select {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
}
</style>
